import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  CheckCircle, MessageCircle, ArrowRight, Calendar, Clock, 
  BookOpen, Users, Mail, Gift, AlertTriangle, Loader2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_skanda-admin/artifacts/473fldg8_skanda.png";

// Brand Colors from Logo
const COLORS = {
  primary: '#0044A7',
  secondary: '#25B283',
  accent: '#32CD99',
  dark: '#1C3B78',
  gradient: 'linear-gradient(135deg, #32CD99 0%, #0044A7 100%)',
};

export default function SAPSuccessFactorsThankYou() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (window.fbq) {
      window.fbq('track', 'CompleteRegistration', {
        content_name: 'SAP SuccessFactors Training',
        status: 'completed'
      });
    }

    // Fetch landing page data for WhatsApp link and batch info
    const fetchPageData = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/landing-pages/sap-successfactors`);
        setPageData(response.data);
      } catch (err) {
        console.error('Error fetching page data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  // Default values if API fails
  const whatsappLink = pageData?.whatsapp_link || 'https://chat.whatsapp.com/B6xqrpnOqUf3czX6BK1FZq';
  const batchStartDate = pageData?.batch_start_date || 'February 15, 2026';
  const batchDuration = pageData?.batch_duration || '8 Weeks';

  const nextSteps = [
    {
      icon: MessageCircle,
      title: 'Join Our Learning Community',
      description: 'Connect with fellow learners, get study materials, and receive course updates',
      action: 'Join WhatsApp Group',
      link: whatsappLink,
      highlight: true
    },
    {
      icon: Calendar,
      title: 'Batch Commencement',
      description: `The next batch begins ${batchStartDate}. Detailed schedule will be shared via email.`,
    },
    {
      icon: BookOpen,
      title: 'Pre-Course Resources',
      description: 'Introductory materials will be sent to your registered email before the course starts.',
    },
    {
      icon: Mail,
      title: 'Counselor Consultation',
      description: 'Our academic counselor will reach out within 24 hours to discuss your learning objectives.',
    }
  ];

  const resources = [
    'Interview Preparation Guide',
    'Resume Templates',
    'Alumni Network Access',
    'Mock Interview Sessions',
    'LinkedIn Profile Tips'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: COLORS.primary }} />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Registration Confirmed | SKANDA IT SOLUTIONS</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
            <a href="/">
              <img src={LOGO_URL} alt="Skanda IT Solutions" className="h-10 w-auto" />
            </a>
          </div>
        </header>

        {/* Success Banner */}
        <div className="py-16" style={{ background: COLORS.gradient }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-6 shadow-lg">
              <CheckCircle size={32} style={{ color: COLORS.secondary }} />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-testid="thank-you-title">
              Registration Confirmed
            </h1>
            <p className="text-white/80 max-w-md mx-auto">
              Thank you for registering for the SAP SuccessFactors Training Program. 
              You're one step closer to advancing your career.
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          {/* WhatsApp CTA */}
          <div className="bg-green-600 rounded-xl p-6 sm:p-8 mb-10 text-white">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shrink-0">
                <MessageCircle size={28} className="text-green-600" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-xl font-semibold mb-2">Join Our Learning Community</h2>
                <p className="text-green-100 text-sm mb-4">
                  Connect with 500+ learners. Get daily insights, study materials, and placement updates.
                </p>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-green-600 rounded-lg font-medium hover:bg-green-50 transition-colors"
                >
                  <MessageCircle size={18} />
                  Join WhatsApp Group
                </a>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-white rounded-xl p-6 sm:p-8 mb-10 shadow-sm">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-3" style={{ color: COLORS.dark }}>
              <Clock size={22} style={{ color: COLORS.secondary }} />
              What Happens Next
            </h2>
            
            <div className="space-y-4">
              {nextSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div 
                    key={index} 
                    className={`flex items-start gap-4 p-4 rounded-lg ${step.highlight ? 'bg-green-50 border border-green-100' : 'bg-slate-50'}`}
                  >
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-white"
                      style={{ background: step.highlight ? '#16a34a' : COLORS.gradient }}
                    >
                      <Icon size={20} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium mb-1" style={{ color: COLORS.dark }}>{step.title}</h3>
                      <p className="text-slate-600 text-sm">{step.description}</p>
                      {step.action && (
                        <a
                          href={step.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-2 text-green-600 text-sm font-medium hover:text-green-700"
                        >
                          {step.action}
                          <ArrowRight size={14} />
                        </a>
                      )}
                    </div>
                    <span className="text-2xl font-bold text-slate-200">{index + 1}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Resources */}
          <div 
            className="rounded-xl p-6 sm:p-8 mb-10 border"
            style={{ backgroundColor: `${COLORS.accent}10`, borderColor: `${COLORS.accent}30` }}
          >
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-3" style={{ color: COLORS.dark }}>
              <Gift size={22} style={{ color: COLORS.primary }} />
              Included Resources
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {resources.map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                  <CheckCircle className="shrink-0" size={18} style={{ color: COLORS.secondary }} />
                  <span className="text-slate-700 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Program Details */}
          <div className="bg-white rounded-xl p-6 sm:p-8 mb-10 shadow-sm">
            <h2 className="text-xl font-semibold mb-6" style={{ color: COLORS.dark }}>Program Details</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="flex items-center gap-4">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${COLORS.accent}15` }}
                >
                  <Calendar size={20} style={{ color: COLORS.primary }} />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Start Date</div>
                  <div className="font-medium" style={{ color: COLORS.dark }}>{batchStartDate}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${COLORS.secondary}15` }}
                >
                  <Clock size={20} style={{ color: COLORS.secondary }} />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Duration</div>
                  <div className="font-medium" style={{ color: COLORS.dark }}>{batchDuration}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${COLORS.accent}15` }}
                >
                  <Users size={20} style={{ color: COLORS.primary }} />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Format</div>
                  <div className="font-medium" style={{ color: COLORS.dark }}>Live Online Sessions</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${COLORS.secondary}15` }}
                >
                  <BookOpen size={20} style={{ color: COLORS.secondary }} />
                </div>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Certificate</div>
                  <div className="font-medium" style={{ color: COLORS.dark }}>Included</div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="rounded-xl p-6 sm:p-8 text-center mb-10 text-white" style={{ backgroundColor: COLORS.dark }}>
            <h2 className="text-lg font-semibold mb-2">Have Questions?</h2>
            <p className="text-slate-300 text-sm mb-4">Our team is available to assist you.</p>
            <a
              href="mailto:info@skandaitsolutions.com"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-lg font-medium hover:bg-slate-100 transition-colors text-sm"
              style={{ color: COLORS.dark }}
            >
              <Mail size={16} />
              Contact Support
            </a>
          </div>

          {/* Disclaimer */}
          <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg mb-10">
            <div className="flex items-start gap-3 text-xs text-amber-800">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <p>
                <strong>Disclaimer:</strong> SKANDA IT SOLUTIONS is an independent training provider. 
                We are not authorized partners or affiliates of SAP SE, Meta Platforms Inc., or any of their subsidiaries.
              </p>
            </div>
          </div>

          {/* Back Link */}
          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors text-sm"
            >
              <ArrowRight size={16} className="rotate-180" />
              Return to Homepage
            </Link>
          </div>
        </div>

        {/* Footer */}
        <footer className="py-8 mt-12" style={{ backgroundColor: COLORS.dark }}>
          <div className="max-w-4xl mx-auto px-4 text-center">
            <img src={LOGO_URL} alt="Skanda IT Solutions" className="h-8 w-auto mx-auto mb-3 bg-white rounded p-1" />
            <p className="text-slate-400 text-xs">© 2026 SKANDA IT SOLUTIONS. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
